import type { CSSProperties, UIEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import type { TFunction } from 'i18next';
import type { ReservationBoard, ReservationBooking } from '../../services/reservation-system/reservation-board.service';
import {
  RS_MAX_RESOURCE_COUNT,
  formatRsLocalTimeRangeDisplay,
  getDayRowBackgroundClass,
  listRsDraftBookingCells,
  scheduleScrollRsCalendarDayIntoView,
  shouldShowRsEmptyBookingsHint,
  type RsBookingSpanGroup,
} from '../../utils/reservationBoardCalendar.util';
import RsCalendarBookingCard from './RsCalendarBookingCard';
import RsCalendarDayRow from './RsCalendarDayRow';
import RsCalendarDraftCard from './RsCalendarDraftCard';
import styles from './ReservationBoardCalendar.module.css';

type RsCalendarGridProps = {
  board: ReservationBoard;
  year: number;
  month: number;
  today: Date;
  daysInMonth: number[];
  holidayByDay: Map<number, string>;
  bookingSpanGroups: RsBookingSpanGroup[];
  gridStyle: CSSProperties;
  isBoardOwner: boolean;
  isLoading: boolean;
  resourceUpdatePending: boolean;
  deletePending: boolean;
  /** フォームで選択中の日（同月内）。ハイライト用 */
  selectedDay?: number | null;
  /** フォームで選択中の項目。選択日のハイライトをこの項目マスに限定する */
  selectedResourceNames?: string[];
  /** 登録前プレビュー（開始・終了・表示名） */
  draftStartDateTime?: string;
  draftEndDateTime?: string;
  draftDisplayName?: string;
  /** 既存予約と重なる項目名（下書きを警告色にする） */
  draftOverlapResourceNames?: string[];
  /** 日付／空きセルクリック → 開始・終了（と任意で項目）をフォームへ反映 */
  onPickSlot?: (day: number, resourceName?: string) => void;
  /** 「続けて予約」→ その直後の空き枠をフォームへ */
  onPrefillAfterBooking?: (booking: ReservationBooking) => void;
  onEditResource: (index: number, name: string) => void;
  onRemoveResource: (index: number) => void;
  onAddResource: () => void;
  onDeleteBooking: (booking: ReservationBooking) => void;
  t: TFunction;
};

export default function RsCalendarGrid({
  board,
  year,
  month,
  today,
  daysInMonth,
  holidayByDay,
  bookingSpanGroups,
  gridStyle,
  isBoardOwner,
  isLoading,
  resourceUpdatePending,
  deletePending,
  selectedDay = null,
  selectedResourceNames = [],
  draftStartDateTime = '',
  draftEndDateTime = '',
  draftDisplayName = '',
  draftOverlapResourceNames = [],
  onPickSlot,
  onPrefillAfterBooking,
  onEditResource,
  onRemoveResource,
  onAddResource,
  onDeleteBooking,
  t,
}: RsCalendarGridProps) {
  const pickHint = t('reservation.board.pickSlotHint', {
    defaultValue: 'クリックして日時をフォームに入れる',
  });
  const gridRootRef = useRef<HTMLDivElement>(null);
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const bodyScrollRef = useRef<HTMLDivElement>(null);
  const syncingScrollRef = useRef(false);

  const syncHorizontalScroll = useCallback((source: 'header' | 'body', scrollLeft: number) => {
    if (syncingScrollRef.current) return;
    const target = source === 'header' ? bodyScrollRef.current : headerScrollRef.current;
    if (!target || target.scrollLeft === scrollLeft) return;
    syncingScrollRef.current = true;
    target.scrollLeft = scrollLeft;
    syncingScrollRef.current = false;
  }, []);

  const onHeaderScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      syncHorizontalScroll('header', e.currentTarget.scrollLeft);
    },
    [syncHorizontalScroll]
  );

  const onBodyScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      syncHorizontalScroll('body', e.currentTarget.scrollLeft);
    },
    [syncHorizontalScroll]
  );

  const draftCells = useMemo(
    () =>
      listRsDraftBookingCells(
        year,
        month,
        draftStartDateTime,
        draftEndDateTime,
        selectedResourceNames,
        board.resourceNames
      ),
    [
      year,
      month,
      draftStartDateTime,
      draftEndDateTime,
      selectedResourceNames,
      board.resourceNames,
    ]
  );
  const draftTimeLabel = formatRsLocalTimeRangeDisplay(
    draftStartDateTime,
    draftEndDateTime,
    t('reservation.board.timeRangeSeparator', { defaultValue: '〜' }),
    t('reservation.board.overnightPrefix', { defaultValue: '翌' })
  );
  const draftNameLabel = draftDisplayName.trim();
  const draftOverlapSet = useMemo(
    () => new Set(draftOverlapResourceNames),
    [draftOverlapResourceNames]
  );
  const draftNestedInBookingKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const cell of draftCells) {
      const nested = bookingSpanGroups.some(
        (group) =>
          group.resourceIndex === cell.resourceIndex &&
          cell.firstDay <= group.lastDay &&
          cell.lastDay >= group.firstDay
      );
      if (nested) keys.add(`${cell.resourceIndex}:${cell.firstDay}:${cell.lastDay}`);
    }
    return keys;
  }, [draftCells, bookingSpanGroups]);

  const renderDraftCard = (resourceName: string | undefined) => {
    if (!draftTimeLabel) return null;
    return (
      <RsCalendarDraftCard
        timeLabel={draftTimeLabel}
        nameLabel={draftNameLabel}
        overlaps={Boolean(resourceName && draftOverlapSet.has(resourceName))}
        t={t}
      />
    );
  };

  useEffect(() => {
    if (isLoading) return;

    const now = new Date();
    const dayToShow =
      selectedDay ??
      (now.getFullYear() === year && now.getMonth() + 1 === month ? now.getDate() : null);
    if (dayToShow == null) return;

    // デスクトップもクランプ付き scroll で選択日を視野へ（sticky サイドバーを共有バー下に維持）
    return scheduleScrollRsCalendarDayIntoView(dayToShow, {
      behavior: 'auto',
      root: gridRootRef.current,
    });
  }, [selectedDay, year, month, isLoading]);

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  const showEmptyBookingsHint = shouldShowRsEmptyBookingsHint({
    bookingGroupCount: bookingSpanGroups.length,
    year,
    month,
    draftStartDateTime,
    draftEndDateTime,
    selectedResourceNames,
  });

  return (
    <div ref={gridRootRef}>
      {showEmptyBookingsHint ? (
        <p className={styles.calendarEmptyBookings} role="status">
          {t('reservation.board.emptyBookingsHint', {
            defaultValue: 'この月の確定予約はまだありません。日付や空きマスをタップして登録できます。',
          })}
        </p>
      ) : null}
      <div
        ref={headerScrollRef}
        className={styles.calendarHeaderSticky}
        data-rs-calendar-header
        onScroll={onHeaderScroll}
      >
        <div className={styles.headerRow} style={gridStyle}>
          <div className={styles.headerDay}>{t('reservation.board.day', { defaultValue: '日' })}</div>
          <div className={styles.headerWeekday}>{t('reservation.board.weekday', { defaultValue: '曜' })}</div>
          {board.resourceNames.map((name, index) => (
            <div key={`${name}-${index}`} className={styles.headerCell}>
              <div className={styles.headerCellInner}>
                <span className={styles.headerCellLabel}>{name}</span>
                {isBoardOwner && (
                  <div className={styles.headerCellActions}>
                    <button
                      type="button"
                      className={`btn btn-sm ${styles.iconActionBtn}`}
                      onClick={() => onEditResource(index, name)}
                      title={t('reservation.board.editResourceTitle', { defaultValue: '項目名の編集' })}
                      aria-label={t('reservation.board.editResourceTitle', { defaultValue: '項目名の編集' })}
                    >
                      <i className="fas fa-pen" aria-hidden />
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${styles.iconActionBtn}`}
                      onClick={() => onRemoveResource(index)}
                      disabled={resourceUpdatePending || board.resourceNames.length <= 1}
                      title={t('reservation.board.removeResourceTitle', { defaultValue: '項目を削除' })}
                      aria-label={t('reservation.board.removeResourceTitle', { defaultValue: '項目を削除' })}
                    >
                      <i className="fas fa-trash-alt" aria-hidden />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
          {isBoardOwner && (
            <div className={styles.headerAddCell}>
              <button
                type="button"
                className={`btn btn-sm ${styles.iconActionBtn}`}
                onClick={() => onAddResource()}
                disabled={resourceUpdatePending || board.resourceNames.length >= RS_MAX_RESOURCE_COUNT}
                title={t('reservation.board.addResourceTitle', { defaultValue: '項目を追加' })}
                aria-label={t('reservation.board.addResourceTitle', { defaultValue: '項目を追加' })}
              >
                <i className="fas fa-plus" aria-hidden />
              </button>
            </div>
          )}
        </div>
      </div>

      <div
        ref={bodyScrollRef}
        className={styles.calendarGridScroll}
        onScroll={onBodyScroll}
      >
      <div className={styles.calendarBody} style={gridStyle}>
        {daysInMonth.map((day) => {
          const weekday = new Date(year, month - 1, day).getDay();
          const isToday =
            day === today.getDate() && month === today.getMonth() + 1 && year === today.getFullYear();
          const holidayName = holidayByDay.get(day);
          return (
            <RsCalendarDayRow
              key={day}
              day={day}
              weekday={weekday}
              holidayName={holidayName}
              todayClass={getDayRowBackgroundClass(weekday, isToday, Boolean(holidayName), styles)}
              isSelectedDay={selectedDay === day}
              selectedResourceNames={selectedResourceNames}
              resourceNames={board.resourceNames}
              isBoardOwner={isBoardOwner}
              pickHint={pickHint}
              onPickSlot={onPickSlot}
              t={t}
            />
          );
        })}

        {bookingSpanGroups.map((group) => {
          const nestedDraft = draftCells.find(
            (cell) =>
              cell.resourceIndex === group.resourceIndex &&
              cell.firstDay <= group.lastDay &&
              cell.lastDay >= group.firstDay
          );
          return (
            <div
              key={`${group.resourceIndex}-${group.firstDay}-${group.lastDay}-${group.bookings.map((b) => b.id).join('-')}`}
              className={`${styles.resourceCell} ${styles.bookingSpanCell}`}
              style={{
                gridRow: `${group.firstDay} / ${group.lastDay + 1}`,
                gridColumn: group.resourceIndex + 3,
              }}
            >
              {group.bookings.map((booking) => (
                <RsCalendarBookingCard
                  key={booking.id}
                  booking={booking}
                  deletePending={deletePending}
                  onPrefillAfterBooking={onPrefillAfterBooking}
                  onDeleteBooking={onDeleteBooking}
                  t={t}
                />
              ))}
              {nestedDraft
                ? renderDraftCard(board.resourceNames[nestedDraft.resourceIndex])
                : null}
            </div>
          );
        })}

        {draftTimeLabel
          ? draftCells
              .filter(
                (cell) =>
                  !draftNestedInBookingKeys.has(
                    `${cell.resourceIndex}:${cell.firstDay}:${cell.lastDay}`
                  )
              )
              .map((cell) => (
                <div
                  key={`draft-${cell.resourceIndex}-${cell.firstDay}-${cell.lastDay}`}
                  className={`${styles.resourceCell} ${styles.bookingSpanCell} ${styles.bookingDraftCell}`}
                  style={{
                    gridRow: `${cell.firstDay} / ${cell.lastDay + 1}`,
                    gridColumn: cell.resourceIndex + 3,
                  }}
                >
                  {renderDraftCard(board.resourceNames[cell.resourceIndex])}
                </div>
              ))
          : null}
      </div>
      </div>
    </div>
  );
}
